import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Info, CheckCircle2, AlertTriangle, Star,Share2 } from 'lucide-react';
import { getAppliedRoleListForDrCr, fetchUserJogetProfileUUDIApi, saveApplicationReview, incrementKnowMoreApi, generateAiAnalysisApi, fetchCurrentAuditionDetailsApi } from '../../services/api'; 
import MetaTags from '../../components/MetaTags'; 
import './ManageApplicationsPage.css'; 
import Swal from "sweetalert2"; 

import HeaderSection from '../../components/applicationDetails/HeaderSection';
import CandidateDetails from '../../components/applicationDetails/CandidateDetails';
import CastingReview from '../../components/applicationDetails/CastingReview';
import MeetingInfo from '../../components/applicationDetails/MeetingInfo';
import { useAuth } from '../../context/AuthContext';

const calculateAge = (dobString) => {
    if (!dobString) return '---';
    const [month, day, year] = dobString.split('/').map(Number);
    const birthDate = new Date(year, month - 1, day); 
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
};

const isMeetingExpired = (meetingEndString, meetingStartString) => {
    const targetDate = meetingEndString || meetingStartString;
    if (!targetDate) return false;
    return new Date().getTime() > new Date(targetDate).getTime();
};

const ManageApplicationDetails = () => {
    const { id: roleApplicationId } = useParams(); 
    const navigate = useNavigate();
    const location = useLocation();
     const { user } = useAuth();
     const hasPermission = (moduleKey, action = 'View') => {
        if (String(user?.roleCode || user?.role_code) === '900') {
            const perms = user?.permissions || [];
            return perms.includes(`${moduleKey}:${action}`);
        }
        return true; // CD (400) has full access
    };
     const isReviewSubmitted = () => {
        if (!selectedApp) return false;
        
        const roleCode = String(user?.roleCode || user?.role_code || '');
        const roleName = String(user?.role || '').toLowerCase();
        
        if (roleCode === '900' || roleName === 'staff') {
            return !!(selectedApp.staffComment && selectedApp.staffComment.trim() !== '');
        } else if (isAdmin) {
            return !!(selectedApp.modified_by_admin && selectedApp.modified_by_admin.trim() !== '');
        } else {
            const cdReview = selectedApp.review || selectedApp.c_review || '';
            return !!(cdReview && cdReview.trim() !== '');
        }
    };


    const [selectedApp, setSelectedApp] = useState(null);
    const [talentProfile, setTalentProfile] = useState(null);
    const [loadingApp, setLoadingApp] = useState(true);
    const [error, setError] = useState('');
    
    const [reviewText, setReviewText] = useState('');
    const [ratingStar, setRatingStar] = useState(0); 
    const [hoverRating, setHoverRating] = useState(0); 
    const [decision, setDecision] = useState(''); 
    const [saveReviewLoading, setSaveReviewLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({}); 

    const adminToken = localStorage.getItem("adminToken");
    const isAdmin = !!adminToken;

    const handleBack = () => {
        if (location.pathname.includes('/admin')) navigate('/admin/audition-list'); 
        else navigate('/dashboard/manage-applications');
    };

      const handleShare = async () => {
        const shareTitle = `Audition Analysis - ${selectedApp?.talent_name || 'Candidate'}`;
        const shareText = `Check out the acting audition analysis report for ${selectedApp?.talent_name || 'Candidate'} - Role: ${selectedApp?.c_role_name || 'N/A'} (Project: ${selectedApp?.c_project_name || 'N/A'})`;
        const shareUrl = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: shareUrl,
                });
            } catch (err) {
                console.log('Share canceled or failed', err);
            }
        } else {
            try {
                await navigator.clipboard.writeText(shareUrl);
                Swal.fire({
                    icon: 'success',
                    title: 'Link Copied!',
                    text: 'Audition report link copied to clipboard.',
                    width: '380px',
                    background: '#131824',
                    color: '#ffffff',
                    confirmButtonColor: '#dc3545',
                    customClass: {
                        title: 'text-white fw-bold fs-5 mb-2',
                        htmlContainer: 'text-muted mb-3 fs-6',
                        confirmButton: 'btn btn-danger px-4 py-2 fw-bold'
                    },
                    buttonsStyling: false
                });
            } catch (err) {
                console.error('Failed to copy link', err);
            }
        }
    };
      
     const renderStatusBadge = (status) => {
        const s = String(status || '').toUpperCase();
        if (s === 'SELECTED') return <span className="badge bg-success fw-bold px-2 py-1">SELECTED</span>;
        if (s === 'REJECTED') return <span className="badge bg-danger fw-bold px-2 py-1">REJECTED</span>;
        if (s === 'RESUBMIT') return <span className="badge bg-warning text-dark fw-bold px-2 py-1">RESUBMIT</span>;
        return <span className="badge bg-secondary fw-bold px-2 py-1">{status || 'PENDING'}</span>;
    };
    // ✅ Dynamic parser for review logs with fallback handling
    const getReviewLogs = () => {
        if (selectedApp?.reviewLogs && selectedApp.reviewLogs.length > 0) {
            return selectedApp.reviewLogs.map(log => {
                let roleName = 'Casting Director';
                const roleCode = String(log.reviewer_role_code || '');
                if (roleCode === '900') roleName = 'Staff';
                else if (roleCode === '100') roleName = 'Admin';
                
                return {
                    role: roleName,
                    reviewerName: log.reviewer_name || 'N/A',
                    comment: log.review || '',
                    rating: parseInt(log.rating_star, 10) || 0,
                    status: log.result || 'Pending',
                    date: log.createdAt ? new Date(log.createdAt).toLocaleString() : 'N/A'
                };
            });
        }
        
        // Fallback mapping for old records
        const logs = [];
        if (selectedApp?.staffComment) {
            logs.push({
                role: 'Staff',
                reviewerName: 'Staff Member',
                comment: selectedApp.staffComment,
                rating: parseInt(selectedApp.ratingStar, 10) || 0,
                status: selectedApp.result || 'Pending',
                date: 'N/A'
            });
        }
        if (selectedApp?.review) {
            logs.push({
                role: 'Casting Director',
                reviewerName: 'Casting Director',
                comment: selectedApp.review,
                rating: parseInt(selectedApp.ratingStar || selectedApp.c_ratingStar, 10) || 0,
                status: selectedApp.result || 'Pending',
                date: 'N/A'
            });
        }
        if (selectedApp?.modified_by_admin) {
            logs.push({
                role: 'Admin (Modified)',
                reviewerName: 'Admin',
                comment: selectedApp.modified_by_admin,
                rating: parseInt(selectedApp.ratingStar || selectedApp.c_ratingStar, 10) || 0,
                status: selectedApp.result || 'Pending',
                date: 'N/A'
            });
        }
        return logs;
    };

    const loadApplicationAndProfile = useCallback(async (isSilent = false) => {
        if (!isSilent) setLoadingApp(true);
        setError('');
        try {
            const detailRes = await fetchCurrentAuditionDetailsApi(roleApplicationId).catch(() => ({ data: { data: {} } }));
            const detailData = detailRes.data?.data || {};

            const response = await getAppliedRoleListForDrCr();
            const rawData = response.data.data || [];
            const foundApp = rawData.find(item => item.roleApplicationId === roleApplicationId);

            if (!foundApp) {
                setError('Application not found.');
                if (!isSilent) setLoadingApp(false);
                return;
            }

            const candidateId = detailData.userId || detailData.c_userId || foundApp.candidateJogetId || foundApp.c_userId || foundApp.userId || foundApp.appliedByUser;
            let aiData = detailData.aiAnalysis || foundApp.aiAnalysis || foundApp.ai_analysis_data;
            if (typeof aiData === 'string') {
                try { aiData = JSON.parse(aiData); } catch(e) { aiData = null; }
            }

            const validStatuses = ["SELECTED", "REJECTED", "RESUBMIT"];
            const rawResult = String(detailData.result || detailData.c_result || foundApp.c_result || foundApp.result || '').toUpperCase();
            const initialDecision = validStatuses.includes(rawResult) ? rawResult : '';

            // const savedReview = detailData.review || detailData.c_review || foundApp.c_review || foundApp.review || '';
            // const savedRating = detailData.ratingStar || detailData.c_ratingStar || foundApp.c_ratingStar || foundApp.ratingStar || '0';

            // setSelectedApp({
            //     ...foundApp,
            //     ...detailData,
            //     talent_name: foundApp.candidateName || detailData.candidateName || 'Candidate',
            //     aiAnalysis: aiData,
            //     c_role_name: detailData.roleName || foundApp.c_roleName || 'N/A',
            //     c_project_name: detailData.projectName || foundApp.projectName || 'N/A',
            //     c_roleScript: detailData.roleScript || foundApp.c_roleScript || 'No script provided.',
            //     c_videoUrl: detailData.videoUrl || foundApp.c_videoUrl || null,
            //     bookingType: detailData.bookingType || foundApp.booking_type || 'VIDEO_UPLOAD',
            //     talent_id: candidateId || foundApp.appliedByUser || detailData.userId,
            //     c_result: initialDecision || 'Pending'
            // });

            // setReviewText(savedReview);
            // setRatingStar(parseInt(savedRating, 10) || 0);
            // setDecision(initialDecision); 
                        // ✅ Determine what comment and rating to display in the form fields based on logged-in user's role
            const roleCode = String(user?.roleCode || user?.role_code || '');
            const roleName = String(user?.role || '').toLowerCase();
            
            let savedReview = '';
            let savedRating = '0';
            
            if (roleCode === '900' || roleName === 'staff') {
                savedReview = detailData.staffComment || foundApp.staffComment || '';
                savedRating = detailData.ratingStar || detailData.c_ratingStar || foundApp.c_ratingStar || foundApp.ratingStar || '0';
            } else if (isAdmin) {
                savedReview = detailData.modified_by_admin || foundApp.modified_by_admin || '';
                savedRating = detailData.ratingStar || detailData.c_ratingStar || foundApp.c_ratingStar || foundApp.ratingStar || '0';
            } else {
                savedReview = detailData.review || detailData.c_review || foundApp.c_review || foundApp.review || '';
                savedRating = detailData.ratingStar || detailData.c_ratingStar || foundApp.c_ratingStar || foundApp.ratingStar || '0';
            }

            setSelectedApp({
                ...foundApp,
                ...detailData,
                talent_name: foundApp.candidateName || detailData.candidateName || 'Candidate',
                aiAnalysis: aiData,
                c_role_name: detailData.roleName || foundApp.c_roleName || 'N/A',
                c_project_name: detailData.projectName || foundApp.projectName || 'N/A',
                c_roleScript: detailData.roleScript || foundApp.c_roleScript || 'No script provided.',
                c_videoUrl: detailData.videoUrl || foundApp.c_videoUrl || null,
                bookingType: detailData.bookingType || foundApp.booking_type || 'VIDEO_UPLOAD',
                talent_id: candidateId || foundApp.appliedByUser || detailData.userId,
                c_result: initialDecision || 'Pending'
            });

            setReviewText(savedReview);
            setRatingStar(parseInt(savedRating, 10) || 0);
            setDecision(initialDecision);

            if (candidateId) {
                const res = await fetchUserJogetProfileUUDIApi(candidateId);
                setTalentProfile(res.data?.jogetData || {});
            }
        } catch (err) {
            setError('Failed to load application details.');
        } finally {
            if (!isSilent) setLoadingApp(false);
        }
    }, [roleApplicationId]);

    useEffect(() => { 
        if (roleApplicationId) loadApplicationAndProfile(); 
    }, [roleApplicationId, loadApplicationAndProfile]);

    const handleSaveReview = async () => {
        const errors = {};
        const validStatuses = ["SELECTED", "REJECTED", "RESUBMIT"];

        if (!ratingStar || ratingStar === 0) {
            errors.ratingStar = "Please select a rating.";
        }
        if (!reviewText || !reviewText.trim()) {
            errors.reviewText = "Please enter review text.";
        }
        if (!decision || !validStatuses.includes(decision.toUpperCase())) {
            errors.decision = "Please select a status.";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return; 
        }

        setFieldErrors({});
        setSaveReviewLoading(true);

        Swal.fire({
            title: 'Saving Review & Status...',
            text: 'Please wait while we update the details.',
            width: '400px',
            padding: '1.5rem',
            background: '#131824',
            color: '#ffffff',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const payload = {                
                roleId: selectedApp.c_roleId || selectedApp.roleId, 
                userId: selectedApp.talent_id || selectedApp.appliedByUser, 
                addedBy: selectedApp.drcrUserId, 
                applicationId: roleApplicationId,                
                review: reviewText,
                ratingStar: ratingStar.toString(),
                result: decision,
                isAdmin: isAdmin,
                userRoleCode: user?.roleCode || user?.role_code || '' 
            };
            const res = await saveApplicationReview(payload);
            
            if (res.data?.success || res.success) {
                await loadApplicationAndProfile(true);

                Swal.fire({ 
                    icon: "success", 
                    title: "Success!", 
                    text: "Review & status saved successfully!",
                    width: '400px',
                    padding: '1.75rem',
                    background: '#131824',
                    color: '#ffffff',
                    confirmButtonColor: "#dc3545",
                    customClass: {
                        title: 'text-white fw-bold fs-5 mb-2',
                        htmlContainer: 'text-muted mb-3 fs-6',
                        confirmButton: 'btn btn-danger px-4 py-2 fw-bold'
                    },
                    buttonsStyling: false
                });
            } else {
                Swal.fire({ 
                    icon: "error", 
                    title: "Failed to Save", 
                    text: res.data?.message || "Could not save review. Please try again.",
                    width: '400px',
                    padding: '1.75rem',
                    background: '#131824',
                    color: '#ffffff',
                    confirmButtonColor: "#dc3545",
                    customClass: {
                        title: 'text-white fw-bold fs-5 mb-2',
                        htmlContainer: 'text-muted mb-3 fs-6',
                        confirmButton: 'btn btn-danger px-4 py-2 fw-bold'
                    },
                    buttonsStyling: false
                });
            }
        } catch (err) {
            Swal.fire({ 
                icon: "error", 
                title: "Error", 
                text: err.response?.data?.message || "Failed to save review.",
                width: '400px',
                padding: '1.75rem',
                background: '#131824',
                color: '#ffffff',
                confirmButtonColor: "#dc3545",
                customClass: {
                    title: 'text-white fw-bold fs-5 mb-2',
                    htmlContainer: 'text-muted mb-3 fs-6',
                    confirmButton: 'btn btn-danger px-4 py-2 fw-bold'
                },
                buttonsStyling: false
            });
        } finally {
            setSaveReviewLoading(false);
        }
    };

    // Candidate Profile Navigation (Sends viewOnly=true & isCD=true)
    const handleViewCandidateProfile = async () => {
        const targetCandidateId = 
            selectedApp?.talent_id || 
            selectedApp?.userId || 
            selectedApp?.appliedByUser || 
            selectedApp?.candidateJogetId || 
            selectedApp?.c_userId ||
            talentProfile?.userId ||
            talentProfile?.id;

        if (!targetCandidateId || targetCandidateId === 'undefined') {
            console.warn("Candidate ID not available");
            Swal.fire({
                icon: 'warning',
                title: 'Notice',
                text: 'Candidate Profile ID is not available.',
                width: '380px',
                background: '#131824',
                color: '#fff'
            });
            return;
        }

        try {
            await incrementKnowMoreApi(targetCandidateId).catch(() => {});
        } catch (err) {
            console.error("Profile view increment error:", err);
        }

        if (location.pathname.includes('/admin')) {
            navigate(`/admin/candidate-profile/${targetCandidateId}?knowMore=true&viewOnly=true`);
        } else {
             navigate(`/dashboard/candidate-profile/${targetCandidateId}?knowMore=true&viewOnly=true&isCD=true`);
        }
    };

    if (loadingApp) return <div className="text-center p-5"><Spinner className="text-white" animation="border" variant="danger" /></div>;
    if (error && !selectedApp) return <Alert variant="danger" className="m-3">{error}</Alert>;
    if (!selectedApp) return <Alert variant="info" className="m-3">Application details could not be loaded.</Alert>;

    const tp = talentProfile || {};
    const talentAge = calculateAge(tp.dateOfBirth);
    const ai = selectedApp.aiAnalysis || {};
    const isVideoUpload = String(selectedApp.bookingType || "").toUpperCase() === "VIDEO_UPLOAD";
    const expired = isMeetingExpired(selectedApp.meetingEnd, selectedApp.meetingStart);

    return (
        <div className="container-fluid py-4 px-xl-5 text-white" style={{ background: '#0b0f19', minHeight: '100vh' }}>
            <MetaTags title="Audition Analysis - BookMyAudition" />
            
            {/* <HeaderSection handleBack={handleBack} selectedApp={selectedApp} /> */}
            <div className="position-relative">
                <HeaderSection handleBack={handleBack} selectedApp={selectedApp} />
                <button 
                    onClick={handleShare}
                    className="btn btn-outline-danger d-flex align-items-center gap-2 fw-bold position-absolute"
                    style={{ 
                        top: '20px', 
                        right: '25px', 
                        zIndex: 10,
                        borderRadius: '8px', 
                        border: '1px solid #dc3545',
                        backgroundColor: 'transparent'
                    }}
                >
                    <Share2 size={16} /> Share Audition
                </button>
            </div>

            {isVideoUpload ? (
                ai ? (
                    <div className="row g-4">
                        {/* COLUMN 1: Video, Script & Candidate Details */}
                        <div className="col-12 col-lg-4 d-flex flex-column gap-4">
                            <div className="card-dark p-3 rounded" style={{background: '#131824', border: '1px solid #21293d'}}>
                                <div className="fw-bold mb-3">Audition Video</div>
                                <div className="position-relative mb-3 bg-black rounded overflow-hidden" style={{aspectRatio: '16/9'}}>
                                    {selectedApp.c_videoUrl || selectedApp.videoUrl ? (
                                        <video controls width="100%" height="100%" src={selectedApp.c_videoUrl || selectedApp.videoUrl} style={{ objectFit: 'contain', backgroundColor: '#000' }} />
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center h-100 text-muted small">No Video Uploaded</div>
                                    )}
                                </div>
                                <div className="row">
                                    <div className="col-6">
                                        <div className="text-primary mb-2" style={{fontSize: '0.9rem'}}>Audition Script</div>
                                        <div className="text-muted mb-0 small" style={{fontSize: '0.75rem', lineHeight: '1.4', maxHeight: '120px', overflowY: 'auto'}} dangerouslySetInnerHTML={{ __html: selectedApp.c_roleScript }} />
                                    </div>
                                    <div className="col-6 border-start border-secondary ps-3">
                                        <div className="text-primary mb-2" style={{fontSize: '0.9rem'}}>Candidate Details</div>
                                        <ul className="list-unstyled mb-0" style={{fontSize: '0.8rem'}}>
                                            <li className="mb-1">
                                                <span className="text-muted d-inline-block" style={{width: '65px'}}>Name:</span> 
                                                <span className="fw-bold text-white" style={{cursor: 'pointer', textDecoration: 'underline'}} onClick={handleViewCandidateProfile}>
                                                    {selectedApp.talent_name}
                                                </span>
                                            </li>
                                            <li className="mb-1"><span className="text-muted d-inline-block" style={{width: '65px'}}>Age:</span> <span className="text-white">{talentAge} Yrs</span></li>
                                            <li className="mb-1"><span className="text-muted d-inline-block" style={{width: '65px'}}>Gender:</span> <span className="text-white">{tp.gender || '---'}</span></li>
                                            <li className="mb-1"><span className="text-muted d-inline-block" style={{width: '65px'}}>Height:</span> <span className="text-white">{tp.height || '---'} cm</span></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Video Quality Analysis */}
                            <div className="card-dark p-3 rounded" style={{background: '#131824', border: '1px solid #21293d'}}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="fw-bold">Video Quality Analysis</div>
                                    <Info size={16} className="text-muted"/>
                                </div>
                                <div className="d-flex flex-column gap-3 mb-3">
                                    {ai.videoQuality?.map((item, idx) => (
                                        <div key={idx} className="d-flex align-items-center">
                                            <div className="text-muted small" style={{width: '130px', fontSize: '0.8rem'}}>{item.l}</div>
                                            <div className="flex-grow-1 mx-2"><div className="progress bg-secondary" style={{height: '4px'}}><div className="bg-success h-100 rounded" style={{width: `${item.s}%`}}></div></div></div>
                                            <div className="text-white small" style={{fontSize: '0.8rem'}}>{item.s}/100</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="text-center p-2 rounded" style={{background: 'rgba(55, 162, 99, 0.1)'}}>
                                    <div className="text-muted small mb-1" style={{fontSize: '0.75rem'}}>Overall Video Quality Score</div>
                                    <div className="text-success fs-4 fw-bold">{ai.videoQualityScore || 0}<span className="fs-6 text-muted">/100</span></div>
                                </div>
                            </div>
                        </div>

                        {/* COLUMN 2: AI Performance & Match Breakdown & AI Explainability */}
                        <div className="col-12 col-lg-4 d-flex flex-column gap-4">
                            <div className="card-dark p-3 rounded" style={{background: '#131824', border: '1px solid #21293d'}}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="fw-bold">AI Performance Analysis</div>
                                    <Info size={16} className="text-muted"/>
                                </div>
                                <div className="d-flex flex-column gap-3 mb-3">
                                    {ai.performanceAnalysis?.map((item, idx) => (
                                        <div key={idx} className="d-flex align-items-center">
                                            <div className="text-muted small" style={{width: '130px', fontSize: '0.8rem'}}>{item.l}</div>
                                            <div className="flex-grow-1 mx-2"><div className="progress bg-secondary" style={{height: '4px'}}><div className="bg-success h-100 rounded" style={{width: `${item.s}%`}}></div></div></div>
                                            <div className="text-white small" style={{fontSize: '0.8rem'}}>{item.s}/100</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="text-center p-2 rounded" style={{background: 'rgba(55, 162, 99, 0.1)'}}>
                                    <div className="text-muted small mb-1" style={{fontSize: '0.75rem'}}>Overall Acting Score</div>
                                    <div className="text-success fs-4 fw-bold mb-1">{ai.actingScore || 0}<span className="fs-6 text-muted">/100</span></div>
                                    <div className="d-flex justify-content-center gap-1 text-warning">{[1,2,3,4,5].map(i => <Star key={i} size={14} fill="currentColor"/>)}</div>
                                </div>
                            </div>

                            <div className="card-dark p-3 rounded" style={{background: '#131824', border: '1px solid #21293d'}}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="fw-bold">Casting Match Breakdown</div>
                                    <Info size={16} className="text-muted"/>
                                </div>
                                <div className="row g-2">
                                    <div className="col-6 d-flex flex-column gap-2">
                                        {ai.castingMatchLeft?.map((item, idx) => (
                                            <div key={idx}><div className="d-flex justify-content-between text-muted" style={{fontSize: '0.75rem'}}><span>{item.l}</span><span className="text-white">{item.s}%</span></div><div className="progress bg-secondary" style={{height: '3px'}}><div className="bg-success h-100" style={{width: `${item.s}%`}}></div></div></div>
                                        ))}
                                    </div>
                                    <div className="col-6 d-flex flex-column gap-2">
                                        {ai.castingMatchRight?.map((item, idx) => (
                                            <div key={idx}><div className="d-flex justify-content-between text-muted" style={{fontSize: '0.75rem'}}><span>{item.l}</span><span className="text-white">{item.s}%</span></div><div className="progress bg-secondary" style={{height: '3px'}}><div className="bg-success h-100" style={{width: `${item.s}%`}}></div></div></div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="card-dark p-3 rounded" style={{background: '#131824', border: '1px solid #21293d'}}>
                                <div className="d-flex justify-content-between align-items-center mb-2"><div className="fw-bold">AI Explainability</div><Info size={16} className="text-muted"/></div>
                                <div className="text-success small fw-semibold mb-1">Strengths</div>
                                <ul className="list-unstyled d-flex flex-column gap-1 mb-3" style={{fontSize: '0.75rem'}}>{(ai.explainabilityStrengths || ["Strong emotional transitions"]).map((str, i) => <li key={i} className="d-flex align-items-center gap-2 text-muted"><CheckCircle2 size={13} className="text-success flex-shrink-0"/> {str}</li>)}</ul>
                                <div className="text-warning small fw-semibold mb-1">Areas to Improve</div>
                                <ul className="list-unstyled d-flex flex-column gap-1 mb-0" style={{fontSize: '0.75rem'}}>{(ai.explainabilityImprovements || ["Slow down dialogue"]).map((imp, i) => <li key={i} className="d-flex align-items-center gap-2 text-muted"><AlertTriangle size={13} className="text-warning flex-shrink-0"/> {imp}</li>)}</ul>
                            </div>
                            
                        </div>

                        {/* COLUMN 3: Score & Review */}
                        {/* <div className="col-12 col-lg-4 d-flex flex-column gap-4">
                            <div className="card-dark p-3 rounded" style={{background: '#131824', border: '1px solid #21293d'}}>
                                <div className="d-flex justify-content-between align-items-center mb-2"><div className="fw-bold">Casting Match Score</div><Info size={16} className="text-muted"/></div>
                                <div className="text-center mb-3">
                                    <div className="position-relative mx-auto mb-2 d-flex align-items-center justify-content-center" style={{width: '90px', height: '90px', borderRadius: '50%', background: `conic-gradient(#37A263 ${ai.castingMatchScore || 93}%, #1F2432 0)`}}>
                                        <div className="bg-dark rounded-circle d-flex align-items-center justify-content-center" style={{width: '72px', height: '72px'}}><span className="fs-4 fw-bold text-white">{ai.castingMatchScore || 93}%</span></div>
                                    </div>
                                    <h6 className="text-success fw-bold mb-0">Excellent Match</h6>
                                    <div className="text-muted small" style={{fontSize: '0.75rem'}}>Candidate fits well with role requirements</div>
                                </div>
                            </div>

                            <CastingReview 
                                bookingType={selectedApp?.bookingType}
                                hoverRating={hoverRating} setHoverRating={setHoverRating}
                                ratingStar={ratingStar} setRatingStar={setRatingStar}
                                reviewText={reviewText} setReviewText={setReviewText}
                                decision={decision} setDecision={setDecision}
                                handleSaveReview={handleSaveReview} saveReviewLoading={saveReviewLoading}
                                fieldErrors={fieldErrors} setFieldErrors={setFieldErrors}
                            />
                        </div> */}
                         <div className="col-12 col-lg-4 d-flex flex-column gap-4">
                            <div className="card-dark p-3 rounded" style={{background: '#131824', border: '1px solid #21293d'}}>
                                <div className="d-flex justify-content-between align-items-center mb-2"><div className="fw-bold">Casting Match Score</div><Info size={16} className="text-muted"/></div>
                                <div className="text-center mb-3">
                                    <div className="position-relative mx-auto mb-2 d-flex align-items-center justify-content-center" style={{width: '90px', height: '90px', borderRadius: '50%', background: `conic-gradient(#37A263 ${ai.castingMatchScore || 93}%, #1F2432 0)`}}>
                                        <div className="bg-dark rounded-circle d-flex align-items-center justify-content-center" style={{width: '72px', height: '72px'}}><span className="fs-4 fw-bold text-white">{ai.castingMatchScore || 93}%</span></div>
                                    </div>
                                    <h6 className="text-success fw-bold mb-0">Excellent Match</h6>
                                    <div className="text-muted small" style={{fontSize: '0.75rem'}}>Candidate fits well with role requirements</div>
                                </div>
                            </div>

                           {hasPermission('Auditions', 'Action') && (
                            <div className="review-card-wrapper" 
                                 style={{ 
                                     // ✅ Removed flex-grow-1 & minHeight so logs fit naturally and don't get pushed/cut off at the bottom
                                     pointerEvents: isReviewSubmitted() ? 'none' : 'auto',
                                     opacity: isReviewSubmitted() ? 0.65 : 1
                                 }}>
                                <CastingReview 
                                    bookingType={selectedApp?.bookingType}
                                    hoverRating={hoverRating} setHoverRating={setHoverRating}
                                    ratingStar={ratingStar} setRatingStar={setRatingStar}
                                    reviewText={reviewText} setReviewText={setReviewText}
                                    decision={decision} setDecision={setDecision}
                                    handleSaveReview={handleSaveReview} saveReviewLoading={saveReviewLoading}
                                    fieldErrors={fieldErrors} setFieldErrors={setFieldErrors}
                                    disabled={isReviewSubmitted()} 
                                />
                            </div>
                            )}
                           {/* {hasPermission('Auditions', 'Action') && (
                            <div className="flex-grow-1 d-flex flex-column review-card-wrapper" style={{ minHeight: '350px' }}>
                                <CastingReview 
                                    bookingType={selectedApp?.bookingType}
                                    hoverRating={hoverRating} setHoverRating={setHoverRating}
                                    ratingStar={ratingStar} setRatingStar={setRatingStar}
                                    reviewText={reviewText} setReviewText={setReviewText}
                                    decision={decision} setDecision={setDecision}
                                    handleSaveReview={handleSaveReview} saveReviewLoading={saveReviewLoading}
                                    fieldErrors={fieldErrors} setFieldErrors={setFieldErrors}
                                />
                            </div>
                            )} */}
                             {/* {(selectedApp?.staffComment || selectedApp?.review || selectedApp?.modified_by_admin) && (
                                <div className="card-dark p-3 rounded mt-3" style={{ background: '#131824', border: '1px solid #21293d' }}>
                                    <div className="fw-bold mb-3 text-danger" style={{ fontSize: '0.9rem' }}>📝 Audition Review Logs</div>
                                    <div className="d-flex flex-column gap-2" style={{ fontSize: '0.85rem' }}>
                                        {selectedApp?.staffComment && (
                                            <div className="p-2 rounded bg-dark border border-secondary">
                                                <span className="fw-bold text-warning">Staff Comment:</span>{' '}
                                                <span className="text-light">{selectedApp.staffComment}</span>
                                            </div>
                                        )}
                                        {selectedApp?.review && (
                                            <div className="p-2 rounded bg-dark border border-secondary">
                                                <span className="fw-bold text-success">Casting Director Comment:</span>{' '}
                                                <span className="text-light">{selectedApp.review}</span>
                                            </div>
                                        )}
                                        {selectedApp?.modified_by_admin && (
                                            <div className="p-2 rounded bg-dark border border-secondary">
                                                <span className="fw-bold text-info">Admin Modified Comment:</span>{' '}
                                                <span className="text-light">{selectedApp.modified_by_admin}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )} */}
                                                         {(selectedApp?.staffComment || selectedApp?.review || selectedApp?.modified_by_admin) && (
                                <div className="card-dark p-3 rounded" style={{ background: '#131824', border: '1px solid #21293d' }}>
                                    <div className="fw-bold mb-3 text-danger" style={{ fontSize: '0.9rem' }}>📝 Audition Review Logs</div>
                                    <div className="table-responsive">
                                        <table className="table table-dark table-bordered align-middle mb-0" style={{ fontSize: '0.825rem', borderColor: '#21293d' }}>
                                            <thead className="table-dark">
                                                <tr style={{ background: '#1c2333', color: '#94a3b8' }}>
                                                    <th>Reviewer Role</th>
                                                    <th>Comment / Review</th>
                                                    <th>Rating</th>
                                                    <th>Decision Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedApp?.staffComment && (
                                                    <tr>
                                                        <td className="text-warning fw-bold">Staff</td>
                                                        <td className="text-light" style={{ minWidth: '150px' }}>{selectedApp.staffComment}</td>
                                                        <td>
                                                            <div className="d-flex gap-1 text-warning">
                                                                {Array.from({ length: 5 }, (_, idx) => (
                                                                    <Star key={idx} size={13} fill={idx < (parseInt(selectedApp.ratingStar, 10) || 0) ? "currentColor" : "none"} />
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td>{renderStatusBadge(selectedApp.result)}</td>
                                                    </tr>
                                                )}
                                                {selectedApp?.review && (
                                                    <tr>
                                                        <td className="text-success fw-bold">Casting Director</td>
                                                        <td className="text-light" style={{ minWidth: '150px' }}>{selectedApp.review}</td>
                                                        <td>
                                                            <div className="d-flex gap-1 text-warning">
                                                                {Array.from({ length: 5 }, (_, idx) => (
                                                                    <Star key={idx} size={13} fill={idx < (parseInt(selectedApp.ratingStar || selectedApp.c_ratingStar, 10) || 0) ? "currentColor" : "none"} />
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td>{renderStatusBadge(selectedApp.result)}</td>
                                                    </tr>
                                                )}
                                                {selectedApp?.modified_by_admin && (
                                                    <tr>
                                                        <td className="text-info fw-bold">Admin (Modified)</td>
                                                        <td className="text-light" style={{ minWidth: '150px' }}>{selectedApp.modified_by_admin}</td>
                                                        <td>
                                                            <div className="d-flex gap-1 text-warning">
                                                                {Array.from({ length: 5 }, (_, idx) => (
                                                                    <Star key={idx} size={13} fill={idx < (parseInt(selectedApp.ratingStar || selectedApp.c_ratingStar, 10) || 0) ? "currentColor" : "none"} />
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td>{renderStatusBadge(selectedApp.result)}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            </div>
                            
                    </div>
                ) : (
                    <div className="text-center py-5 card-dark p-5 rounded bg-dark border border-secondary text-white">
                        <h5 className="text-warning">⏳ AI Audition Analysis Pending</h5>
                        <p className="text-muted small mt-2">{selectedApp.c_videoUrl || selectedApp.videoUrl ? "Click below to generate AI analysis report now." : "Candidate has not uploaded an audition video yet."}</p>
                        {(selectedApp.c_videoUrl || selectedApp.videoUrl) && (
                            <button className="btn btn-danger btn-sm mt-3 fw-bold" onClick={async () => {
                                try {
                                    Swal.fire({ title: 'Analyzing with Gemini AI...', text: 'Please wait...', didOpen: () => Swal.showLoading() });
                                    const response = await generateAiAnalysisApi(roleApplicationId);
                                    if(response.data?.success || response.success) {
                                        Swal.fire({ icon: 'success', title: 'AI Report Generated!', text: 'Reloading page...' });
                                        window.location.reload();
                                    } else {
                                        Swal.fire({ icon: 'error', title: 'Failed', text: response.data?.message || 'Could not generate AI report' });
                                    }
                                } catch(err) {
                                    Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Something went wrong' });
                                }
                            }}>
                                ⚡ Generate AI Analysis Now
                            </button>
                        )}
                    </div>
                )
            ) : (
                 <div className="row g-4">
                    <div className="col-12 col-lg-4 d-flex flex-column gap-4">
                        <MeetingInfo selectedApp={selectedApp} expired={expired} />
                        <CandidateDetails 
                            selectedApp={selectedApp} 
                            talentAge={talentAge} 
                            tp={tp} 
                            handleViewCandidateProfile={handleViewCandidateProfile} 
                        />
                    </div>

                    {/* <div className="col-12 col-lg-8 d-flex flex-column gap-4">
                        <div className="card-dark p-4 rounded" style={{background: '#131824', border: '1px solid #21293d'}}>
                            <h5 className="text-danger fw-bold mb-2">📍 Audition Booking & Meeting Information</h5>
                            <p className="text-muted small mb-0">This candidate booked an interactive slot or face-to-face audition. Please evaluate the candidate during the session and submit your review below.</p>
                        </div>
                        {hasPermission('Auditions', 'Action') && (
                        <CastingReview 
                            bookingType={selectedApp?.bookingType}
                            hoverRating={hoverRating} setHoverRating={setHoverRating}
                            ratingStar={ratingStar} setRatingStar={setRatingStar}
                            reviewText={reviewText} setReviewText={setReviewText}
                            decision={decision} setDecision={setDecision}
                            handleSaveReview={handleSaveReview} saveReviewLoading={saveReviewLoading}
                            fieldErrors={fieldErrors} setFieldErrors={setFieldErrors}
                        />
                        )} */}

                                            <div className="col-12 col-lg-8 d-flex flex-column gap-4">
                        <div className="card-dark p-4 rounded" style={{background: '#131824', border: '1px solid #21293d'}}>
                            <h5 className="text-danger fw-bold mb-2">📍 Audition Booking & Meeting Information</h5>
                            <p className="text-muted small mb-0">This candidate booked an interactive slot or face-to-face audition. Please evaluate the candidate during the session and submit your review below.</p>
                        </div>
                        {hasPermission('Auditions', 'Action') && (
                        // ✅ Disable pointer events and set opacity if review is already submitted
                        <div style={{ 
                            pointerEvents: isReviewSubmitted() ? 'none' : 'auto',
                            opacity: isReviewSubmitted() ? 0.65 : 1
                        }}>
                            <CastingReview 
                                bookingType={selectedApp?.bookingType}
                                hoverRating={hoverRating} setHoverRating={setHoverRating}
                                ratingStar={ratingStar} setRatingStar={setRatingStar}
                                reviewText={reviewText} setReviewText={setReviewText}
                                decision={decision} setDecision={setDecision}
                                handleSaveReview={handleSaveReview} saveReviewLoading={saveReviewLoading}
                                fieldErrors={fieldErrors} setFieldErrors={setFieldErrors}
                                disabled={isReviewSubmitted()} // ✅ Pass disabled prop
                            />
                        </div>
                        )}
                         {/* {(selectedApp?.staffComment || selectedApp?.review || selectedApp?.modified_by_admin) && (
                            <div className="card-dark p-3 rounded" style={{ background: '#131824', border: '1px solid #21293d' }}>
                                <div className="fw-bold mb-3 text-danger" style={{ fontSize: '0.9rem' }}>📝 Audition Review Logs</div>
                                <div className="d-flex flex-column gap-2" style={{ fontSize: '0.85rem' }}>
                                    {selectedApp?.staffComment && (
                                        <div className="p-2 rounded bg-dark border border-secondary">
                                            <span className="fw-bold text-warning">Staff Comment:</span>{' '}
                                            <span className="text-light">{selectedApp.staffComment}</span>
                                        </div>
                                    )}
                                    {selectedApp?.review && (
                                        <div className="p-2 rounded bg-dark border border-secondary">
                                            <span className="fw-bold text-success">Casting Director Comment:</span>{' '}
                                            <span className="text-light">{selectedApp.review}</span>
                                        </div>
                                    )}
                                    {selectedApp?.modified_by_admin && (
                                        <div className="p-2 rounded bg-dark border border-secondary">
                                            <span className="fw-bold text-info">Admin Modified Comment:</span>{' '}
                                            <span className="text-light">{selectedApp.modified_by_admin}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )} */}
                                                 {(selectedApp?.staffComment || selectedApp?.review || selectedApp?.modified_by_admin) && (
                            <div className="card-dark p-3 rounded" style={{ background: '#131824', border: '1px solid #21293d' }}>
                                <div className="fw-bold mb-3 text-danger" style={{ fontSize: '0.9rem' }}>📝 Audition Review Logs</div>
                                <div className="table-responsive">
                                    <table className="table table-dark table-bordered align-middle mb-0" style={{ fontSize: '0.825rem', borderColor: '#21293d' }}>
                                        <thead className="table-dark">
                                            <tr style={{ background: '#1c2333', color: '#94a3b8' }}>
                                                <th>Reviewer Role</th>
                                                <th>Comment / Review</th>
                                                <th>Rating</th>
                                                <th>Decision Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedApp?.staffComment && (
                                                <tr>
                                                    <td className="text-warning fw-bold">Staff</td>
                                                    <td className="text-light" style={{ minWidth: '150px' }}>{selectedApp.staffComment}</td>
                                                    <td>
                                                        <div className="d-flex gap-1 text-warning">
                                                            {Array.from({ length: 5 }, (_, idx) => (
                                                                <Star key={idx} size={13} fill={idx < (parseInt(selectedApp.ratingStar, 10) || 0) ? "currentColor" : "none"} />
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td>{renderStatusBadge(selectedApp.result)}</td>
                                                </tr>
                                            )}
                                            {selectedApp?.review && (
                                                <tr>
                                                    <td className="text-success fw-bold">Casting Director</td>
                                                    <td className="text-light" style={{ minWidth: '150px' }}>{selectedApp.review}</td>
                                                    <td>
                                                        <div className="d-flex gap-1 text-warning">
                                                            {Array.from({ length: 5 }, (_, idx) => (
                                                                <Star key={idx} size={13} fill={idx < (parseInt(selectedApp.ratingStar || selectedApp.c_ratingStar, 10) || 0) ? "currentColor" : "none"} />
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td>{renderStatusBadge(selectedApp.result)}</td>
                                                </tr>
                                            )}
                                            {selectedApp?.modified_by_admin && (
                                                <tr>
                                                    <td className="text-info fw-bold">Admin (Modified)</td>
                                                    <td className="text-light" style={{ minWidth: '150px' }}>{selectedApp.modified_by_admin}</td>
                                                    <td>
                                                        <div className="d-flex gap-1 text-warning">
                                                            {Array.from({ length: 5 }, (_, idx) => (
                                                                <Star key={idx} size={13} fill={idx < (parseInt(selectedApp.ratingStar || selectedApp.c_ratingStar, 10) || 0) ? "currentColor" : "none"} />
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td>{renderStatusBadge(selectedApp.result)}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageApplicationDetails;